import {
  NewButton,
  NewButtonSize,
  NewButtonVariant,
} from "@visiblethread/react-components";
import clsx from "clsx";
import Flex, { GAP_CLASS } from "components/Flex";
import { ALIGN_CLASS, DIRECTION_CLASS } from "components/Flex/Flex";
import UserAvatar from "components/UserAvatar";
import { Pencil, Reply, Trash2 } from "lucide-react";
import { arrayOf, bool, func, number, shape, string } from "prop-types";
import { DATE_VARIANTS, formatDate } from "utils";
import { toInitials } from "utils/toInitials";

import CommentBody from "./CommentBody";
import CommentComposer from "./CommentComposer";
import classes from "./CommentThreadPanel.module.scss";

const CommentEntry = ({
  comment,
  isReply,
  editState,
  onStartReply,
  onStartEdit,
  onDelete,
}) => {
  if (comment.isDeleted) {
    return (
      <div data-testid="comment-deleted">
        <p className={classes["comment__deleted"]}>Comment deleted</p>
      </div>
    );
  }

  const {
    id,
    author,
    body,
    mentions,
    createdAt,
    editedAt,
    canEdit,
    canDelete,
  } = comment;
  const isEditing = editState.commentId === id;

  return (
    <div className={classes["comment__entry"]}>
      <UserAvatar
        initials={toInitials(author?.displayName)}
        name={author?.displayName}
      />
      <Flex alignItems={ALIGN_CLASS.center} gap={GAP_CLASS.GAP_200}>
        <span className={classes["comment__author"]}>
          {author?.displayName}
        </span>
        <span className={classes["comment__timestamp"]}>
          {formatDate(createdAt, DATE_VARIANTS.DOT_TIME)}
        </span>
        <Flex gap={GAP_CLASS.GAP_100} className={classes["comment__actions"]}>
          {!isReply && (
            <NewButton
              variant={NewButtonVariant.l2}
              size={NewButtonSize.sm}
              leftIcon={<Reply size={16} aria-hidden="true" />}
              onClick={() => onStartReply(id)}
            >
              Reply
            </NewButton>
          )}
          {canEdit && (
            <NewButton
              variant={NewButtonVariant.l2}
              size={NewButtonSize.sm}
              leftIcon={<Pencil size={16} />}
              aria-label="Edit Comment"
              className={classes["comment__icon_button"]}
              onClick={() => onStartEdit(comment)}
            />
          )}
          {canDelete && (
            <NewButton
              variant={NewButtonVariant.l2}
              size={NewButtonSize.sm}
              leftIcon={<Trash2 size={16} />}
              aria-label="Delete Comment"
              className={clsx(
                classes["comment__icon_button"],
                classes["comment__delete_button"]
              )}
              onClick={() => onDelete(comment)}
            />
          )}
        </Flex>
      </Flex>
      <div className={classes["comment__content"]}>
        {isEditing ? (
          <CommentComposer
            value={editState.body}
            onChange={editState.onChange}
            onSubmit={editState.onSubmit}
            onCancel={editState.onCancel}
            submitLabel="Save"
            autoFocus
          />
        ) : (
          <CommentBody body={body} mentions={mentions} editedAt={editedAt} />
        )}
      </div>
    </div>
  );
};

const CommentListItem = ({
  thread,
  replyState,
  editState,
  onStartReply,
  onStartEdit,
  onDelete,
}) => {
  const { comment, replies } = thread;
  const hasReplies = replies?.length > 0;
  const isReplyingToThread =
    !comment.isDeleted && replyState.commentId === comment.id;

  const replyComposer = (
    <div className={classes["comment__content"]}>
      <CommentComposer
        value={replyState.body}
        onChange={replyState.onChange}
        onSubmit={replyState.onSubmit}
        onCancel={replyState.onCancel}
        placeholder="Write a reply…"
        submitLabel="Reply"
        autoFocus
      />
    </div>
  );

  return (
    <Flex
      direction={DIRECTION_CLASS.column}
      alignItems={ALIGN_CLASS.stretch}
      gap={GAP_CLASS.GAP_400}
    >
      <CommentEntry
        comment={comment}
        isReply={false}
        editState={editState}
        onStartReply={onStartReply}
        onStartEdit={onStartEdit}
        onDelete={onDelete}
      />
      {(hasReplies || isReplyingToThread) && (
        <Flex
          direction={DIRECTION_CLASS.column}
          alignItems={ALIGN_CLASS.stretch}
          gap={GAP_CLASS.GAP_300}
          className={classes["thread__replies"]}
        >
          {hasReplies &&
            replies.map((reply) => (
              <CommentEntry
                key={reply.id}
                comment={reply}
                isReply
                editState={editState}
                onStartReply={onStartReply}
                onStartEdit={onStartEdit}
                onDelete={onDelete}
              />
            ))}
          {isReplyingToThread && replyComposer}
        </Flex>
      )}
    </Flex>
  );
};

const commentShape = shape({
  id: number.isRequired,
  parentId: number,
  author: shape({ userName: string, displayName: string }),
  body: string,
  mentions: arrayOf(
    shape({
      token: string.isRequired,
      userName: string,
      displayName: string,
      active: bool,
    })
  ),
  createdAt: string,
  editedAt: string,
  isDeleted: bool,
  isMigrated: bool,
  canEdit: bool,
  canDelete: bool,
});

const composerStateShape = shape({
  commentId: number,
  body: string,
  onChange: func.isRequired,
  onSubmit: func.isRequired,
  onCancel: func.isRequired,
});

CommentEntry.propTypes = {
  comment: commentShape.isRequired,
  isReply: bool.isRequired,
  editState: composerStateShape.isRequired,
  onStartReply: func.isRequired,
  onStartEdit: func.isRequired,
  onDelete: func.isRequired,
};

CommentListItem.propTypes = {
  thread: shape({
    comment: commentShape.isRequired,
    replies: arrayOf(commentShape),
  }).isRequired,
  replyState: composerStateShape.isRequired,
  editState: composerStateShape.isRequired,
  onStartReply: func.isRequired,
  onStartEdit: func.isRequired,
  onDelete: func.isRequired,
};

export default CommentListItem;
